import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, Eye } from 'lucide-react';
import { testimonials } from '../mock';

const TestimonialSection = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-20 bg-white border-b-2 border-kato-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16 animate-fadeInUp">
          <h2 className="text-4xl md:text-5xl font-black text-kato-navy mb-4">
            What Our <span className="text-kato-cyan">Partners</span> Say
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Real testimonials from TikTok creators and business owners who love our Swedish-style candy
          </p>
        </div>

        <div className="relative">
          {/* Extended Share Your Experience Background */}
          <div className="bg-kato-light-gray rounded-xl p-12 lg:pr-96 min-h-[500px] flex items-center">
            <div className="max-w-3xl">
              <h3 className="text-5xl font-black text-kato-navy mb-8">
                Share Your Experience
              </h3>
              <p className="text-gray-600 mb-8 text-xl leading-relaxed">
                Tag us on TikTok @KatoMito and we'll feature your content here! Join our community of candy lovers and business partners sharing their sweet success stories. From retail displays to customer reactions, we love seeing how our Swedish candies bring joy to your business and customers.
              </p>
              <p className="text-gray-500 mb-8 text-lg leading-relaxed">
                Whether it's a viral TikTok moment, customer testimonials, or creative displays - your stories help us grow our sweet community together!
              </p>
              <button className="bg-kato-cyan text-white px-10 py-5 rounded-2xl font-black text-xl hover:bg-opacity-90 transition-colors hover:scale-105">
                Upload Testimonial 📱
              </button>
            </div>
          </div>

          {/* Overlaid Mobile Phone Display */}
          <div className="absolute top-8 right-8 h-auto flex items-start hidden lg:flex">
            <div className="w-80">
              <div className="bg-black rounded-3xl p-1 shadow-2xl">
                {/* Phone Frame */}
                <div className="bg-black rounded-3xl overflow-hidden">
                  {/* Status Bar */}
                  <div className="bg-black text-white px-4 py-1.5 flex justify-between items-center text-xs">
                    <span className="font-medium">9:41</span>
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-1.5 bg-white rounded-sm"></div>
                      <div className="w-1 h-1.5 bg-white rounded-sm"></div>
                      <div className="w-5 h-1.5 bg-white rounded-sm"></div>
                    </div>
                  </div>

                  {/* TikTok Header */}
                  <div className="bg-black text-white px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <img
                        src={testimonials[currentTestimonial].avatar}
                        alt={testimonials[currentTestimonial].name}
                        className="w-8 h-8 rounded-full border-2 border-kato-cyan"
                      />
                      <div>
                        <div className="font-semibold text-xs">
                          {testimonials[currentTestimonial].name}
                        </div>
                        <div className="text-xs text-gray-400">
                          {testimonials[currentTestimonial].platform}
                        </div>
                      </div>
                    </div>
                    <div className="text-kato-cyan text-xs font-medium">
                      Follow
                    </div>
                  </div>

                  {/* Content Area */}
                  <div className="bg-gradient-to-b from-gray-900 to-black text-white p-4 min-h-[320px] flex flex-col justify-between">
                    <div></div>
                    
                    {/* Testimonial Content */}
                    <div className="space-y-3">
                      <p className="text-white text-sm leading-relaxed">
                        {testimonials[currentTestimonial].content}
                      </p>
                      
                      {/* Engagement Stats */}
                      <div className="flex items-center space-x-3 text-xs text-gray-300">
                        <div className="flex items-center space-x-1">
                          <Eye className="w-3 h-3" />
                          <span>{testimonials[currentTestimonial].views}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Heart className="w-3 h-3 text-red-500" />
                          <span>{testimonials[currentTestimonial].likes}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-center space-x-6 pt-3">
                      <button className="flex flex-col items-center space-y-0.5">
                        <Heart className="w-6 h-6 text-white" />
                        <span className="text-xs text-gray-400">Like</span>
                      </button>
                      <button className="flex flex-col items-center space-y-0.5">
                        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                          <span className="text-black text-xs">💬</span>
                        </div>
                        <span className="text-xs text-gray-400">Comment</span>
                      </button>
                      <button className="flex flex-col items-center space-y-0.5">
                        <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                          <span className="text-black text-xs">↗</span>
                        </div>
                        <span className="text-xs text-gray-400">Share</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-center items-center space-x-3 mt-6">
                <button
                  onClick={prevTestimonial}
                  className="p-2 rounded-full bg-kato-navy text-white hover:bg-opacity-80 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                
                <div className="flex space-x-1.5">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentTestimonial ? 'bg-kato-cyan' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={nextTestimonial}
                  className="p-2 rounded-full bg-kato-navy text-white hover:bg-opacity-80 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;